// ViewUtils.jsx
// Shared presentational components and utilities used by both
// WorkspacesSection and BatchJobsSection.
import PropTypes from "prop-types";
import {
  Typography,
  CircularProgress,
  Alert,
  Box,
  Button,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  FONT,
  WEIGHT,
  INK,
  INK_MUTED,
  HAIRLINE,
  HOVER,
  SHADOW_LG,
  STATUS,
  withAlpha,
} from "../../../../theme/tokens";

// Shared props for the row "actions" popover menus. Drops below and aligns to
// the right edge of the trigger, with a soft rounded surface and compact,
// token-styled items (overrides the heavier global MuiMenuItem defaults).
export const ACTION_MENU_PROPS = {
  anchorOrigin: { vertical: "bottom", horizontal: "right" },
  transformOrigin: { vertical: "top", horizontal: "right" },
  slotProps: {
    paper: {
      elevation: 0,
      sx: {
        mt: 0.75,
        minWidth: 208,
        borderRadius: 2,
        border: `1px solid ${HAIRLINE}`,
        boxShadow: SHADOW_LG,
        overflow: "hidden",
        "& .MuiList-root": { py: 0.5 },
        "& .MuiMenuItem-root": {
          fontSize: FONT.md,
          color: INK,
          mx: 0.5,
          px: 1,
          py: 0.75,
          borderRadius: 1.5,
          "&:hover": { backgroundColor: HOVER },
        },
        // Destructive actions (delete / terminate / remove): add
        // className="destructive" to the MenuItem. The icon inherits this color
        // (StyledListItemIcon is color: inherit).
        "& .MuiMenuItem-root.destructive": {
          color: STATUS.error,
          "&:hover": { backgroundColor: withAlpha(STATUS.error, 0.08) },
        },
      },
    },
  },
};

export const formatTimestamp = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleString();
  } catch {
    return "N/A";
  }
};

// Compact human duration ("2d 3h", "4h 5m", "10m") from a seconds value.
export const formatDuration = (seconds) => {
  if (seconds == null || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// Shared empty-state block (icon + title + subtitle) used by the experiment
// list tables so "no rows" / "no matches" states read consistently.
export const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <Box
    sx={{
      py: 6,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
    }}
  >
    {Icon ? <Icon sx={{ fontSize: 40, color: INK_MUTED }} /> : null}
    <Typography
      sx={{ fontSize: FONT.md, fontWeight: WEIGHT.semibold, color: INK }}
    >
      {title}
    </Typography>
    {subtitle ? (
      <Typography
        sx={{ fontSize: FONT.sm, color: INK_MUTED, textAlign: "center" }}
      >
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);
EmptyState.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
};

export const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: "auto",
  marginRight: theme.spacing(1.5),
  color: "inherit",
}));

export const ProgressMessage = ({ children }) => (
  <Box
    sx={{
      p: 2,
      m: 2,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
      backgroundColor: (theme) =>
        theme.palette.mode === "dark" ? "grey.900" : "grey.50",
    }}
  >
    <CircularProgress size={24} sx={{ mr: 1.5 }} />
    <Typography>{children}</Typography>
  </Box>
);
ProgressMessage.propTypes = {
  children: PropTypes.node.isRequired,
};

export const PodLogsView = ({ logContent, logError, logLoading = false }) =>
  logError ? (
    <Alert severity="error">{logError}</Alert>
  ) : (
    <Box
      component="pre"
      sx={{
        // Fill the drawer's content area (which has a bounded height) so the
        // log uses all available space; minHeight keeps it usable on short
        // viewports, where the drawer content scrolls instead.
        height: "100%",
        minHeight: 400,
        background: "#181a20",
        color: "#e0e0e0",
        fontFamily: `'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'Liberation Mono', 'monospace'`,
        fontSize: "1rem",
        borderRadius: 2,
        p: 2,
        overflow: "auto",
        whiteSpace: "pre-wrap",
        boxShadow: 1,
        m: 0,
      }}
    >
      {logLoading && !logContent
        ? "Loading..."
        : logContent || "No logs available."}
    </Box>
  );
PodLogsView.propTypes = {
  logContent: PropTypes.string,
  logError: PropTypes.string,
  logLoading: PropTypes.bool,
};

export const ConfirmTerminateDialog = ({
  open,
  title,
  description,
  onClose,
  onConfirm,
  confirmLabel = "Terminate",
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText>{description}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        variant="outlined"
        color="error"
        onClick={onConfirm}
        sx={{ color: "error.main" }}
        autoFocus
      >
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);
ConfirmTerminateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmLabel: PropTypes.string,
};
